import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "../../../Api/Api";
import endpoints from "../../../Api/Endpoints";

/** Normalize server validation / error payloads into a single string for UI + Redux. */
export function extractApiErrorMessage(error, fallback = "Request failed") {
  const d = error?.response?.data;
  if (d && typeof d === "object") {
    if (typeof d.message === "string" && d.message.trim()) return d.message.trim();
    if (typeof d.error === "string" && d.error.trim()) return d.error.trim();
    if (Array.isArray(d.errors) && d.errors.length) {
      const parts = d.errors
        .map((e) => {
          if (!e || typeof e !== "object") return "";
          const msg = e.msg || e.message || e.type || "";
          const path = e.path || e.param || e.field || "";
          return path && msg ? `${path}: ${msg}` : String(msg || "");
        })
        .filter(Boolean);
      if (parts.length) return parts.join(" ");
    }
  }
  const m = error?.message;
  if (typeof m === "string" && m.trim()) return m.trim();
  return fallback;
}

/** POST presign → PUT to S3 → returns imageKey. Throws if S3 path is required but fails. */
async function presignAndUploadCategoryImage(file) {
  const imageFilename = file.name || "image.jpg";
  const presignRes = await API.post(endpoints.ADMIN_CATEGORIES_UPLOAD_URL, {
    imageFilename,
  });
  const uploadUrl = presignRes?.data?.uploadUrl;
  const key = presignRes?.data?.key;
  if (!uploadUrl || !key) {
    throw new Error("Could not get upload URL");
  }
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "image/jpeg" },
  });
  if (!putRes.ok) {
    throw new Error(`Image upload failed (${putRes.status})`);
  }
  return key;
}

/** Returns S3 key, or null to fall back to multipart (e.g. S3 not configured on server). */
async function tryPresignedCategoryImageKey(file) {
  try {
    return await presignAndUploadCategoryImage(file);
  } catch (err) {
    const status = err?.response?.status;
    const msg = String(err?.response?.data?.message || "").toLowerCase();
    if (status === 400 && (msg.includes("s3") || msg.includes("requires"))) {
      return null;
    }
    throw err;
  }
}

export const getAdminCategories = createAsyncThunk(
  "category/getAdminCategories",
  async ({ page = 1, limit = 12 } = {}, { rejectWithValue }) => {
    try {
      const res = await API.get(endpoints.ADMIN_CATEGORIES, {
        params: { page, limit },
      });
      return res?.data || {};
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to load categories"));
    }
  }
);

export const getAdminSubcategories = createAsyncThunk(
  "category/getAdminSubcategories",
  async ({ categoryId = "", parentSubCategoryId = "", level = "", page = 1, limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const safeLimit = Math.max(1, Number(limit) || 100);
      const params = { page, limit: safeLimit };
      if (categoryId) params.categoryId = categoryId;
      if (parentSubCategoryId) params.parentSubCategoryId = parentSubCategoryId;
      if (level !== "" && level != null) params.level = level;

      const firstRes = await API.get(endpoints.ADMIN_SUBCATEGORIES, { params });
      const firstData = firstRes?.data || {};
      const firstItems = Array.isArray(firstData.items)
        ? firstData.items
        : (Array.isArray(firstData.data) ? firstData.data : []);
      const total = Number(firstData.total || firstItems.length || 0);

      if (firstItems.length >= total) {
        return { ...firstData, items: firstItems };
      }

      const merged = [...firstItems];
      const totalPages = Math.max(1, Math.ceil(total / safeLimit));
      for (let p = Number(page) + 1; p <= totalPages; p += 1) {
        const nextRes = await API.get(endpoints.ADMIN_SUBCATEGORIES, {
          params: { ...params, page: p, limit: safeLimit },
        });
        const nextData = nextRes?.data || {};
        const nextItems = Array.isArray(nextData.items)
          ? nextData.items
          : (Array.isArray(nextData.data) ? nextData.data : []);
        merged.push(...nextItems);
      }

      return { ...firstData, page: 1, limit: merged.length, items: merged, total: merged.length };
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to load subcategories"));
    }
  }
);

export const createAdminCategory = createAsyncThunk(
  "category/createAdminCategory",
  async ({ name, image }, { rejectWithValue }) => {
    try {
      if (image instanceof File) {
        const imageKey = await tryPresignedCategoryImageKey(image);
        if (imageKey) {
          const res = await API.post(endpoints.ADMIN_CATEGORIES, {
            name,
            imageKey,
          });
          return res?.data || {};
        }
        const formData = new FormData();
        formData.append("name", name);
        formData.append("image", image);
        const res = await API.post(endpoints.ADMIN_CATEGORIES, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return res?.data || {};
      }

      const res = await API.post(endpoints.ADMIN_CATEGORIES, { name });
      return res?.data || {};
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to create category"));
    }
  }
);

export const createAdminSubcategory = createAsyncThunk(
  "category/createAdminSubcategory",
  async (payload, { rejectWithValue }) => {
    try {
      const body = {
        categoryId: payload.categoryId,
        name: payload.name,
        parentSubCategoryId: payload.parentSubCategoryId || null,
        description: payload.description,
        lowestPrice: Number(payload.lowestPrice),
        averagePrice: Number(payload.averagePrice),
        highestPrice: Number(payload.highestPrice),
      };

      const res = await API.post(endpoints.ADMIN_SUBCATEGORIES, body);
      return res?.data || {};
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to create subcategory"));
    }
  }
);

export const updateAdminCategory = createAsyncThunk(
  "category/updateAdminCategory",
  async ({ categoryId, name, image, imageOverride }, { rejectWithValue }) => {
    try {
      const url = `${endpoints.ADMIN_CATEGORIES}/${categoryId}`;
      if (image instanceof File) {
        const imageKey = await tryPresignedCategoryImageKey(image);
        if (imageKey) {
          const res = await API.put(url, { name, imageKey });
          return { ...(res?.data || {}), _id: categoryId };
        }
        const formData = new FormData();
        formData.append("name", name);
        formData.append("image", image);
        const res = await API.put(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return { ...(res?.data || {}), _id: categoryId };
      }

      const body = { name };
      if (imageOverride !== undefined) body.image = imageOverride;
      const res = await API.put(url, body);
      return { ...(res?.data || {}), _id: categoryId };
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to update category"));
    }
  }
);

export const deleteAdminCategory = createAsyncThunk(
  "category/deleteAdminCategory",
  async (categoryId, { rejectWithValue }) => {
    try {
      const res = await API.delete(`${endpoints.ADMIN_CATEGORIES}/${categoryId}`);
      return {
        ...(res?.data || {}),
        deletedId: categoryId,
      };
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to delete category"));
    }
  }
);

export const updateAdminSubcategory = createAsyncThunk(
  "category/updateAdminSubcategory",
  async (
    {
      subcategoryId,
      categoryId,
      name,
      parentSubCategoryId,
      description,
      lowestPrice,
      averagePrice,
      highestPrice,
    },
    { rejectWithValue }
  ) => {
    try {
      const body = {
        categoryId,
        name,
        parentSubCategoryId:
          parentSubCategoryId !== undefined ? (parentSubCategoryId || null) : undefined,
        description: description ?? "",
        lowestPrice: Number(lowestPrice),
        averagePrice: Number(averagePrice),
        highestPrice: Number(highestPrice),
      };
      const res = await API.put(
        `${endpoints.ADMIN_SUBCATEGORIES}/${subcategoryId}`,
        body
      );
      return res?.data || {};
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to update subcategory"));
    }
  }
);

export const deleteAdminSubcategory = createAsyncThunk(
  "category/deleteAdminSubcategory",
  async (subcategoryId, { rejectWithValue }) => {
    try {
      await API.delete(`${endpoints.ADMIN_SUBCATEGORIES}/${subcategoryId}`);
      return { deletedId: subcategoryId };
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to delete subcategory"));
    }
  }
);
